import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { getBackendErrorDetails } from '../config/backend-error-messages.config';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BackendErrorMessageModalComponent } from '../../components/backend-error-message-modal/backend-error-message-modal.component';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';

function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
    const httpError = <HttpErrorResponse>error;
    return !!httpError.name && httpError.name === 'HttpErrorResponse';
}

@Injectable()
export class HttpOnlineInterceptor implements HttpInterceptor {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly connectivityService: ConnectivityService,
        private readonly modalService: BsModalService,
        private readonly paceInsightsService: PaceInsightsService,
    ) {}

    /**
     * This function intercepts all HTTP traffic using the normal angular HttpClient module (but not the offline store
     * since it not using the angular API!). It runs the request and checks the response
     *
     * @param req the request to be sent
     * @param next the next handler
     * @returns {Observable<HttpEvent<unknown>>} the observable of the request
     */
    public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let modifiedHeaders = req.headers
            .set('Content-Type', ConfigConstants.jsonContent)
            .set('Accept', 'application/json');

        if (req.body) {
            modifiedHeaders = modifiedHeaders.set('X-Requested-With', 'XMLHttpRequest');
        }

        const requestUrl = req.url;

        const clonedReq = req.clone({ headers: modifiedHeaders, url: requestUrl });

        // Next start request by calling 'next.handle'. When an error occurs, check the response for authentication prompt
        return next.handle(clonedReq).pipe(
            catchError((error: unknown) => {
                const isOnline = this.connectivityService.isInternetConnectionAvailable();

                let requestId = this.paceInsightsService.getTraceId();

                if (isHttpErrorResponse(error)) {
                    const backendRequestId =
                        error.headers?.get('x-ms-request-id') ||
                        error.headers?.get('request-id') ||
                        error.headers?.get('traceparent');
                    if (backendRequestId && backendRequestId !== requestId) {
                        requestId = `${requestId} (Backend: ${backendRequestId})`;
                    }
                }

                if (isHttpErrorResponse(error) && isOnline) {
                    const statusCode = error.status || 0;
                    const customErrorDetails = getBackendErrorDetails(statusCode);

                    this.modalService.show(BackendErrorMessageModalComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            errorData: {
                                customErrorDetails,
                                rawError: error,
                            },
                            supportId: requestId,
                        },
                        ariaLabelledBy: 'error-message-modal',
                    });
                }

                this.log.error(
                    `HttpOnlineInterceptor.intercept(): UNHANDLED ERROR IN HTTP REQUEST ${JSON.stringify(error)}`,
                );

                if (isHttpErrorResponse(error)) {
                    this.paceInsightsService.trackDependency({
                        id: requestId,
                        target: new URL(req.url, window.location.origin).host,
                        name: `${req.method} ${new URL(req.url, window.location.origin).pathname}`,
                        data: req.url,
                        duration: 0, // Duration isn't easily available here without a start timer, 0 is acceptable for failures
                        responseCode: error.status || 0,
                        success: false,
                        type: 'Ajax',
                    });
                }

                return throwError(() => error);
            }),
        );
    }
}
